import { Box, SxProps, Theme, Typography } from "@mui/material";
import ButtonComponent from "components/button/ButtonComponent";
import ImageComponent from "components/image/ImageComponent";
import LabelHeader from "components/Label/LabelHeader";
import { useMemo } from "react";
import { formatWheelRimDiamDisplay } from "utils";
import { NewQuoteMaker } from "../types";

/**
 * CardCarModel component renders a card with car model information.
 */
export default function CardCarModel({
  isFromCarType,
  newQuoteMaker,
  tireSizeText,
  showRimDiameterField = false,
  backRoute,
  sx,
}: {
  isFromCarType: boolean;
  newQuoteMaker?: NewQuoteMaker;
  tireSizeText: string;
  /** When false, hides ハブ径 (e.g. `mst-car-model/result` / EstResult). */
  showRimDiameterField?: boolean;
  backRoute: () => void;
  sx?: SxProps<Theme>;
}) {
  const renderSelectedCar = useMemo(() => {
    if (!newQuoteMaker) return "";
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { car_model_and_period, modl_name } = newQuoteMaker;
    if (car_model_and_period.includes(modl_name)) {
      const periodPart = car_model_and_period.replace(modl_name, "").trim();
      return (
        <>
          {modl_name} <br />
          {periodPart}
        </>
      );
    }
    return car_model_and_period;
  }, [newQuoteMaker?.car_model_and_period, newQuoteMaker?.modl_name]);

  return (
    <Box sx={{ display: "flex", gap: "34px", ...sx }}>
      <Box sx={{ width: { sm: "130px", lg: "162px" } }}>
        {isFromCarType && newQuoteMaker && (
          <ImageComponent
            sx={{ width: { sm: "130px", lg: "162px" } }}
            src={newQuoteMaker.car_image_url}
            alt="car"
            objectFit="contain"
          />
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            width: "100%",
            justifyContent: "flex-end",
            gap: { sm: "16px", lg: "2px" },
          }}
        >
          {isFromCarType && newQuoteMaker && (
            <Box
              sx={{
                minWidth: 260,
                width: { sm: "100%", md: "300px", lg: "374px" },
                gap: "12px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <LabelHeader label="選択車両" sxBox={{ ml: 0, justifyContent: "center" }} />
              <Box sx={{ display: "flex", justifyContent: "center", gap: "30px" }}>
                <Typography
                  sx={{ textAlign: "center", fontSize: { xs: "16px", md: "18px", xl: "20px" } }}
                >
                  {renderSelectedCar}
                </Typography>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              minWidth: 260,
              width: { sm: "100%", md: "300px", lg: "374px" },
              gap: "12px",
              display: "flex",
              flexDirection: "column",
              flexWrap: "wrap",
            }}
          >
            <LabelHeader label="タイヤサイズ" sxBox={{ ml: 0, justifyContent: "center" }} />
            <Box sx={{ display: "flex", justifyContent: "center", gap: "30px" }}>
              <Typography sx={{ fontSize: { xs: "16px", md: "18px", xl: "20px" } }}>
                {tireSizeText}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            justifyContent: !showRimDiameterField ? "flex-end" : "space-between",
            width: "100%",
            gap: { xs: 1, sm: 2 },
          }}
        >
          {showRimDiameterField && (
            <Box
              sx={{
                minWidth: 260,
                width: { sm: "100%", md: "300px", lg: "374px" },
                display: "flex",
                flexDirection: "column",
                flexWrap: "wrap",
              }}
            >
              <LabelHeader label="ハブ径" sxBox={{ ml: 0, justifyContent: "center" }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  minHeight: "51px",
                  alignItems: "center",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    textAlign: "center",
                    fontSize: { xs: "16px", md: "18px", xl: "20px" },
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatWheelRimDiamDisplay(newQuoteMaker?.selectedTire?.mmm_hub1)}
                </Typography>
              </Box>
            </Box>
          )}
          <ButtonComponent
            label="商品選択へ戻る"
            sx={{
              width: { sm: "180px", lg: "212px" },
              height: "51px",
              flexShrink: 0,
            }}
            onClick={backRoute}
          />
        </Box>
      </Box>
    </Box>
  );
}
